import {
  AssignmentTypeEnum,
  RouteFieldTesterSuggestionDTO,
  RoutePackageDTO,
  RoutePlanRideAlongCarrierPackageDTO,
  UserModel,
} from 'api/model';
import { useRoutePlanningEvaluateFieldTester } from 'api/route-planning/route-planning';
import Modal from 'components/Modal';
import PrioritizedCaseAssignmentUserPicker from 'components/PrioritizedCaseAssignmentUserPicker';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import { describeEvaluateFieldTesterError } from './feedback-helpers';
import LoadPill from './LoadPill';
import { formatDurationMs } from './selection-helpers';
import WarningPill, { PillRow } from './WarningPills';
import formatCandidateAnchors from './anchor-helpers';
import {
  describeOtherStatus,
  describeSpareTime,
} from './candidate-choice-helpers';

/** " · 1 h 30 min kvar efter dagen" — det som avgör om dagen går att förlänga med henne. */
const spareTime = (candidate: RouteFieldTesterSuggestionDTO): string => {
  const text = describeSpareTime(candidate.spareMinutesAfterDay);
  return text ? ` · ${text}` : '';
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 560px;
`;

const SectionTitle = styled.h4`
  margin: 0;
`;

const SectionHint = styled.p`
  margin: 0;
  font-size: 0.85em;
  color: rgb(120, 120, 120);
`;

const CandidateRow = styled.button<{ current?: boolean }>`
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid ${({ current }) => (current ? 'rgb(204,102,51)' : '#b5b3b35c')};
  border-radius: 4px;
  background: ${({ current }) => (current ? 'rgba(204,102,51,0.06)' : '#fff')};
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #ecf6ff;
  }
`;

const CandidateName = styled.span`
  font-weight: 700;
`;

const CandidateMeta = styled.span`
  color: rgb(120, 120, 120);
  font-size: 0.85em;
`;

const EvaluatingOverlay = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  color: rgb(120, 120, 120);
`;

interface Props {
  pkg: RoutePackageDTO;
  planDate: Date;
  currentUserId: number | null;
  /** De andra paketens ben och valda förare — så att omvärderingen kan föreslå samåkning. */
  rideAlongCarriers: RoutePlanRideAlongCarrierPackageDTO[];
  onPicked: (candidate: RouteFieldTesterSuggestionDTO) => void;
  onClose: () => void;
}

/**
 * "Byt fälttestare": förberäknade kandidater överst (byte utan anrop), därunder ALLA
 * fälttestare via den befintliga väljaren från caseformuläret (paketet mappas som
 * pseudo-uppdrag). Fritt val omvärderas på servern — pendling, gaps, EU-561 osv.
 */
const FieldTesterSwitchModal: React.FC<Props> = ({
  pkg,
  planDate,
  currentUserId,
  rideAlongCarriers,
  onPicked,
  onClose,
}) => {
  const evaluateMutation = useRoutePlanningEvaluateFieldTester();
  const [evaluatingName, setEvaluatingName] = useState<string | null>(null);
  const firstLeg = pkg.legs[0];
  const lastLeg = pkg.legs[pkg.legs.length - 1];
  const others = pkg.otherFieldTesters ?? [];

  const evaluateAndPick = async (
    userModel: Pick<UserModel, 'userID' | 'name'>
  ) => {
    setEvaluatingName(userModel.name);
    try {
      const suggestion = await evaluateMutation.mutateAsync({
        data: {
          date: planDate,
          fieldTesterId: userModel.userID,
          assignmentIds: pkg.legs.map((l) => l.assignmentId),
          packageIndex: pkg.packageIndex,
          rideAlongCarriers,
        },
      });
      onPicked(suggestion);
    } catch (error) {
      toast.error(describeEvaluateFieldTesterError(error), { duration: 8000 });
    } finally {
      setEvaluatingName(null);
    }
  };

  return (
    <Modal onClose={onClose} title={`Byt fälttestare — paket ${pkg.packageIndex + 1}`}>
      <Wrapper>
        <div>
          <SectionTitle>Föreslagna</SectionTitle>
          <SectionHint>
            Föruträknade kandidater — byte sker direkt utan omräkning.
          </SectionHint>
        </div>
        {pkg.candidates.map((candidate) => (
          <CandidateRow
            current={candidate.userId === currentUserId}
            key={candidate.userId}
            onClick={() => {
              onPicked(candidate);
            }}
            type="button"
          >
            <CandidateName>{candidate.fullName}</CandidateName>
            <CandidateMeta>
              {formatCandidateAnchors(candidate)}{' '}
              · rank {candidate.rank}
              {candidate.commuteToFirst &&
                ` · ${formatDurationMs(candidate.commuteToFirst.durationMs)} pendling`}
              {candidate.proximityKm != null &&
                ` · ${Math.round(candidate.proximityKm)} km bort`}
              {spareTime(candidate)}
              {candidate.rankOneInPackageIndex != null &&
                ` · förstaval i paket ${candidate.rankOneInPackageIndex + 1}`}
            </CandidateMeta>
            {/* Det TL efterfrågade: se belastningen DÄR bytet görs, inte i en annan vy. */}
            <LoadPill
              availabilityWindow={candidate.availabilityWindow}
              hoursLast28Days={candidate.hoursLast28Days}
              loadPercent={candidate.loadPercent}
              maxHoursPer4Weeks={candidate.maxHoursPer4Weeks}
            />
            {candidate.warnings.length > 0 && (
              <PillRow>
                {candidate.warnings.map((w) => (
                  <WarningPill key={w} warning={w} />
                ))}
              </PillRow>
            )}
          </CandidateRow>
        ))}

        {others.length > 0 && (
          <>
            <div>
              <SectionTitle>Andra lediga</SectionTitle>
              <SectionHint>
                De närmaste som inte föreslogs, och varför. Ett val räknas om
                för paketet.
              </SectionHint>
            </div>
            {others.map((other) => (
              <CandidateRow
                disabled={!!evaluatingName}
                key={other.userId}
                onClick={() =>
                  evaluateAndPick({ userID: other.userId, name: other.fullName })
                }
                type="button"
              >
                <CandidateName>{other.fullName}</CandidateName>
                <CandidateMeta>
                  {other.proximityKm != null &&
                    `${Math.round(other.proximityKm)} km bort · `}
                  {describeOtherStatus(
                    other,
                    (index) => `paket ${index + 1}`
                  )}
                  {other.availabilityWindow &&
                    ` · ${other.availabilityWindow}`}
                </CandidateMeta>
              </CandidateRow>
            ))}
          </>
        )}

        <div>
          <SectionTitle>Alla fälttestare</SectionTitle>
          <SectionHint>
            Fritt val — pendling, luckor och körtidsregler räknas om för paketet
            (paketets fönster: {pkg.legs.length} uppdrag,{' '}
            {formatDurationMs(pkg.totalOccupiedMs)} uppdragstid).
          </SectionHint>
        </div>

        {evaluatingName ? (
          <EvaluatingOverlay>
            <LoadingSpinner /> Utvärderar {evaluatingName}…
          </EvaluatingOverlay>
        ) : (
          <PrioritizedCaseAssignmentUserPicker
            assignmentId={firstLeg.assignmentId}
            assignmentType={AssignmentTypeEnum.Normal}
            bookedTo={new Date(lastLeg.bookedTo).toISOString()}
            caseModel={{
              caseTypeID: firstLeg.caseTypeID,
              isElectric: pkg.requiresElectric,
              vehicleGearBox: pkg.requiresManual,
              isPremiumCar: pkg.requiresPremium,
              isTrailer: pkg.requiresTrailer,
              isTruck: pkg.requiresTruck,
            }}
            estimatedDuration={pkg.totalOccupiedMs}
            estimatedStartTime={new Date(pkg.windowStart).toISOString()}
            fromZipCode={firstLeg.fromZip}
            onUserPicked={(userModel) => {
              if (userModel) {
                evaluateAndPick(userModel);
              }
            }}
            requireExistingAssignmentToSelectFT={false}
            selectedUserId={currentUserId}
          />
        )}
      </Wrapper>
    </Modal>
  );
};

export default FieldTesterSwitchModal;
